import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import {
  Route,
  Switch,
  useRouteMatch,
  useLocation,
  useParams
} from "react-router-dom";

import { updateActiveTab } from "actions/filterActions.js";
import { buyplanIdSelector, buyplanStateSelector } from "selectors/buyplanIdSelector.js";
import { smTypeSelector } from "selectors/buyplanInfoSelector.js";
import { stockModelIdSelector } from "selectors/stockModelIdSelector.js";

import ProductReleaseContainer from "containers/ProductReleaseContainer.jsx";
import QuantityGridContainer from "containers/QuantityGridContainer.jsx";
import ViewAtsContainer from "containers/ViewAtsContainer.jsx";

import BsNavPills from "components/BsNavPills.js";

import {
  trackAnalyticsEvent,
  buyplanOrReleaseRecord,
  buyplanContext
} from "apis/analyticsHelpers.js";

const TabsList = ({ tabsConfig }) => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const { hash } = useLocation();
  const { releaseId, brandId } = useParams();
  // Not useParams()'s buyplanId — AppLayout resolves a real buyplan via
  // find_or_create_buyplan regardless of whether the URL has a :buyplanId
  // segment (/releases/:releaseId/products vs .../products/for/:buyplanId),
  // so the Redux-confirmed id is what's actually available on both route
  // shapes.
  const confirmedBuyplanId = useSelector(buyplanIdSelector);
  const buyplanState = useSelector(buyplanStateSelector);
  const smType = useSelector(smTypeSelector);
  const stockModalId = useSelector(stockModelIdSelector);
  const tabIds = useMemo(() => Object.keys(tabsConfig), [tabsConfig]);
  var defaultTabId = ["#buyplan_detail", "#qfill_detail"].includes(hash)
    ? tabIds[1]
    : tabIds[0];
  defaultTabId = ["#showSummary"].includes(hash)
    ? tabIds[2]
    : defaultTabId;
  const [activeTabId, setActiveTabId] = useState(defaultTabId);
  const [tabBody, setTabBody] = useState();

  const changeActiveTab = evt => {
    const selectedTabId = evt.target.dataset.tabId;
    setActiveTabId(selectedTabId);

    if (selectedTabId === "report" && (confirmedBuyplanId || releaseId || brandId)) {
      trackAnalyticsEvent("range_page_action", {
        category: "Report",
        ...buyplanOrReleaseRecord(confirmedBuyplanId, releaseId, brandId),
        ...buyplanContext(confirmedBuyplanId, stockModalId, buyplanState, smType),
        path: window.location.pathname
      });
    }
  };

  // Only dispatch updateActiveTab when the active tab actually changes.
  // Keeping tabsConfig out of this effect's deps prevents spurious
  // filterActions.setActiveTab → isDataLoading:true dispatches that occur
  // whenever the parent re-renders and creates a new tabsConfig reference.
  useEffect(() => {
    dispatch(updateActiveTab(activeTabId));
  }, [dispatch, activeTabId]);

  // Sync the rendered tab body whenever the active tab or the config changes.
  useEffect(() => {
    const component = activeTabId && tabsConfig[activeTabId].body;
    setTabBody(component);
  }, [activeTabId, tabsConfig]);

  return (
    <>
      <BsNavPills>
        {tabIds.map(k => {
          return (
            <NavItem key={k}>
              <NavLink
                className={activeTabId === k ? "active" : ""}
                data-tab-id={k}
                onClick={changeActiveTab}
              >
                {tabsConfig[k].title}
              </NavLink>
            </NavItem>
          );
        })}
      </BsNavPills>

      <TabContent activeTab={activeTabId}>
        <TabPane tabId={activeTabId}>
          {tabBody}

          <Switch>
            <Route
              path={`${path}/:productReleaseId/quick-view`}
              exact
              component={ProductReleaseContainer}
            />

            <Route
              path={`${path}/:productReleaseId/quantity-grid`}
              exact
              component={QuantityGridContainer}
            />

            <Route
              path={`${path}/:productId/ats-view`}
              exact
              component={ViewAtsContainer}
            />
          </Switch>
        </TabPane>
      </TabContent>
    </>
  );
};

export default React.memo(TabsList);
